import { Box, Typography, Grid, Button } from '@mui/material';
import { PageHeader } from '@/components/ui/PageHeader.tsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InvestmentCard from '@/features/investments/InvestmentCard.tsx';
import InvestorInvestmentsEmptyState from '@/features/investments/list/investor/InvestorInvestmentsEmptyState.tsx';
import UpgradeWallModal from '@/features/billing/components/UpgradeWallModal.tsx';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';
import { useInvestorInvestmentAccess } from '@/features/billing/useInvestorInvestmentAccess.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { InvoiceDraftsPanel } from '@/features/investment-transactions/InvoiceDraftsPanel';

const InvestorInvestmentsList = () => {
  const {
    investments,
    lockedInvestmentIds,
    canCreateInvestment,
    shouldShowUpgradePrompt,
    isLoading,
    upgradeWallInvestmentCount,
    upgradeWallPlansPath,
  } = useInvestorInvestmentAccess();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { show: showSnackbar } = useSnackbar();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const checkoutHandled = useRef(false);

  useEffect(() => {
    if (checkoutHandled.current) return;
    if (searchParams.get('checkout') !== 'success') return;

    checkoutHandled.current = true;
    showSnackbar({
      title: 'Suscripción activada',
      message: 'Ya puedes gestionar todas tus inversiones.',
      variant: 'success',
    });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, showSnackbar]);

  useEffect(() => {
    if (isLoading || !investments) {
      return;
    }

    trackProductEvent(trackEvent, 'portfolio_viewed', {
      role: 'investor',
      has_investments: investments.length > 0,
      investment_count: investments.length,
      platform: ANALYTICS_PLATFORM,
    });
  }, [investments, isLoading, trackEvent]);

  const hasLockedInvestments = useMemo(
    () => lockedInvestmentIds.length > 0 || shouldShowUpgradePrompt,
    [lockedInvestmentIds.length, shouldShowUpgradePrompt]
  );

  const handleAddInvestmentClick = () => {
    trackProductEvent(trackEvent, 'add_investment_clicked', {
      entry_point: 'portfolio',
      role: 'investor',
      platform: ANALYTICS_PLATFORM,
    });

    if (!canCreateInvestment) {
      setUpgradeDialogOpen(true);
      return;
    }

    navigate('/inversiones/nueva/', { state: { entryPoint: 'portfolio' } });
  };

  return (
    <Box>
      <UpgradeWallModal
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        onViewPlans={() => {
          setUpgradeDialogOpen(false);
          navigate(upgradeWallPlansPath);
        }}
        investmentCount={upgradeWallInvestmentCount}
      />
      <PageHeader
        title="Mis Inversiones"
        action={
          <Button variant="contained" onClick={handleAddInvestmentClick}>
            Registrar inversión
          </Button>
        }
      />
      <InvoiceDraftsPanel investments={investments ?? []} />
      {isLoading ? (
        <Typography>Cargando inversiones...</Typography>
      ) : (
        <Grid container spacing={3}>
          {investments && investments.length > 0 ? (
            investments.map((inv) => {
              const isLocked = lockedInvestmentIds.includes(inv.id);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={inv.id}>
                  <InvestmentCard
                    investment={inv}
                    disabled={isLocked}
                    onDisabledClick={
                      hasLockedInvestments ? () => setUpgradeDialogOpen(true) : undefined
                    }
                  />
                </Grid>
              );
            })
          ) : (
            <InvestorInvestmentsEmptyState />
          )}
        </Grid>
      )}
    </Box>
  );
};

export default InvestorInvestmentsList;
